﻿// ============================================================================
// MÃ“DULO: DOCUMENT FACTORY HUB (V4.2 - SMART WIZARD & UNIVERSAL SEARCH)
// ARQUIVO: src/components/DocumentFactoryHub.jsx
// OBJETIVO: Ingestão de Arquivos com Classificação CNJ e Vínculo Processual.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StepWizard, StepPanel } from '../components/StepWizard';
import { ContractFactory } from '../features/contracts/ContractEngine';
import { ProcurationFactory } from '../features/procurations/ProcurationEngine';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { auth, storage } from '../shared/api/firebase/config';
import { saveLegalDocument } from '../services/DocumentRepository';
import { FIREBASE_CONFIG } from '../config/Config';
import {
  fetchProcessClientId,
  loadClientsAndProcesses,
  updateDocument,
  addClientNote,
  watchDocuments,
  deleteDocument,
} from '../services/DocumentFactoryHubRepository.js';
import CadernoSelector from './ui/CadernoSelector';
import { toTitleCasePt } from '../utils/formatters';

const appId = FIREBASE_CONFIG.projectId;

const NON_OWNER_LABELS = new Set(['', 'SEM DONO', 'NAO VINCULADO', 'NÃƒO VINCULADO', 'NULL', 'UNDEFINED']);

const normalizeSelectedContext = (selectedContext) => {
    const rawType = String(selectedContext?.type || '').trim();
    const normalizedType = rawType.toUpperCase();
    const id = String(selectedContext?.id || '').trim();
    const name = String(selectedContext?.name || '').trim();

    if (!id) {
        return {
            id: '',
            name: '',
            rawType,
            normalizedType: '',
            isClient: false,
            isProcess: false,
        };
    }

    const isClient = normalizedType === 'CLIENTE' || normalizedType === 'CLIENT' || normalizedType === 'CLIENTE/CRM';
    const isProcess = normalizedType === 'PROCESSO' || normalizedType === 'PROCESS' || normalizedType === 'MATTER';
    const driveParentId = String(
        selectedContext?.driveParentId ||
        selectedContext?.attachmentsFolderId ||
        selectedContext?.driveFolderId ||
        selectedContext?.driveFolderInput ||
        ''
    ).trim();

    return {
        id,
        name,
        rawType,
        normalizedType,
        isClient,
        isProcess,
        driveParentId,
    };
};

const buildLinkedDocumentPayload = ({ docRow, selectedContext, targetClientId }) => {
    const ctx = normalizeSelectedContext(selectedContext);
    const relatedEntityIds = Array.from(new Set([ctx.id, targetClientId].filter(Boolean)));
    const relatedEntities = [];

    if (targetClientId) {
        relatedEntities.push({
            id: targetClientId,
            type: 'client',
            name: ctx.isClient ? (ctx.name || null) : null,
        });
    }

    if (ctx.id) {
        relatedEntities.push({
            id: ctx.id,
            type: ctx.isProcess ? 'matter' : 'client',
            name: ctx.name || null,
        });
    }

    const normalizedRelatedEntities = Array.from(
        new Map(
            relatedEntities
                .filter((item) => String(item?.id || '').trim())
                .map((item) => [String(item.id).trim(), item])
        ).values()
    );

    const payload = {
        status: 'VINCULADO',
        linkedClient: ctx.name || null,
        relatedEntities: normalizedRelatedEntities,
                'context.primaryEntityId': ctx.id || null,
        'context.primaryEntityName': ctx.name || null,
        'context.primaryEntityType': ctx.normalizedType || null,
        'context.relatedEntities': relatedEntityIds,
        'context.uploadedBy': docRow?.context?.uploadedBy || docRow?.uploadedBy || 'Sistema',
    };

    if (ctx.isClient) {
        payload.clientId = ctx.id;
        payload.matterId = null;
    } else if (ctx.isProcess) {
        payload.matterId = ctx.id;
        payload.clientId = targetClientId || null;
    } else if (targetClientId) {
        payload.clientId = targetClientId;
    }

    return payload;
};

const deriveUploadCenterDisplayState = (data) => {
    const primaryEntityName = String(data?.context?.primaryEntityName || '').trim();
    const linkedClient = String(data?.linkedClient || '').trim();
    const primaryEntityId = String(data?.context?.primaryEntityId || '').trim();
    const clientId = String(data?.clientId || '').trim();
    const matterId = String(data?.matterId || '').trim();
    const topLevelStatus = String(data?.status || '').trim().toUpperCase();
    const relatedEntities = Array.isArray(data?.context?.relatedEntities)
        ? data.context.relatedEntities
        : Array.isArray(data?.relatedEntities)
            ? data.relatedEntities
            : [];

    const normalizedPrimaryName = primaryEntityName.toUpperCase();
    const normalizedLinkedClient = linkedClient.toUpperCase();
    const hasOwnerName = !NON_OWNER_LABELS.has(normalizedPrimaryName) || !NON_OWNER_LABELS.has(normalizedLinkedClient);
    const hasContextLink = Boolean(primaryEntityId || clientId || matterId || relatedEntities.length);
    const isLinked = topLevelStatus === 'VINCULADO' || hasContextLink || hasOwnerName;
    const ownerName = !NON_OWNER_LABELS.has(normalizedPrimaryName)
        ? primaryEntityName
        : !NON_OWNER_LABELS.has(normalizedLinkedClient)
            ? linkedClient
            : 'Sem Dono';

    return {
        ownerName,
        status: isLinked ? 'VINCULADO' : 'TRIAGEM',
        isLinked,
    };
};

const resolveProcessClientId = async (processId) => {
    return fetchProcessClientId(processId, appId);
};

// --- COMPONENTE: BUSCA UNIVERSAL (CLIENTES + PROCESSOS) ---
const ContextSearch = ({ onSelect }) => {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pickerTerm, setPickerTerm] = useState('');

    const rootRef = useRef(null);
    const [clientsCache, setClientsCache] = useState([]);
    const [processCache, setProcessCache] = useState([]);
    const [cacheLoading, setCacheLoading] = useState(false);

    const clientsCacheRef = useRef([]);
    const processCacheRef = useRef([]);
    const cacheLoadingRef = useRef(false);

    const norm = (v) => {
        return String(v || '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
    };

    const loadCache = async () => {
        if (cacheLoadingRef.current) {
            return {
                clients: Array.isArray(clientsCacheRef.current) ? clientsCacheRef.current : [],
                processes: Array.isArray(processCacheRef.current) ? processCacheRef.current : [],
            };
        }
        if (
            (Array.isArray(clientsCacheRef.current) && clientsCacheRef.current.length) ||
            (Array.isArray(processCacheRef.current) && processCacheRef.current.length)
        ) {
            return {
                clients: clientsCacheRef.current,
                processes: processCacheRef.current,
            };
        }

        cacheLoadingRef.current = true;
        setCacheLoading(true);

        let clientsRows = [];
        let procRows = [];

        try {
            const cache = await loadClientsAndProcesses(appId);
            clientsRows = cache.clientsRows;
            procRows = cache.procRows;
        } catch (e) {
            clientsRows = [];
            procRows = [];
        }

        clientsCacheRef.current = Array.isArray(clientsRows) ? clientsRows : [];
        processCacheRef.current = Array.isArray(procRows) ? procRows : [];
        setClientsCache(clientsCacheRef.current);
        setProcessCache(processCacheRef.current);

        cacheLoadingRef.current = false;
        setCacheLoading(false);

        return {
            clients: clientsCacheRef.current,
            processes: processCacheRef.current,
        };
    };

    const handleSearch = async (opts) => {
        const options = opts && typeof opts === 'object' ? opts : {};
        const t = String(term || '').trim();

        setSearching(true);
        try {
            const cache = await loadCache();
            const resultsBuffer = [];

            if (!t) {
                if (!options.showAllWhenEmpty) {
                    setResults([]);
                    return;
                }

                const baseClients = Array.isArray(cache?.clients) ? cache.clients : [];
                baseClients.slice(0, 30).forEach((data) => {
                    const name =
                        data?.qualification?.physical?.name ||
                        data?.qualification?.legal?.companyName ||
                        data?.anchor?.name ||
                        data?.name ||
                        'Sem Nome';
                    const docNum = data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '';

                    resultsBuffer.push({
                        id: data.id,
                        name: name,
                        type: 'CLIENTE',
                        doc: docNum,
                        icon: 'fa-user'
                    });
                });

                const baseProc = Array.isArray(cache?.processes) ? cache.processes : [];
                baseProc.slice(0, 20).forEach((data) => {
                    const cnj = data?.numeroCNJ || '';
                    const parte = data?.parteContraria || '';
                    const clientName = data?.clientName || '';
                    resultsBuffer.push({
                        id: data.id,
                        name: `${clientName} vs ${parte}`,
                        type: 'PROCESSO',
                        doc: cnj,
                        icon: 'fa-gavel'
                    });
                });

                setResults(resultsBuffer.slice(0, 60));
                return;
            }

            const lowerTerm = norm(t);

            const baseClients = Array.isArray(cache?.clients) ? cache.clients : [];
            baseClients.forEach((data) => {
                const name =
                    data?.qualification?.physical?.name ||
                    data?.qualification?.legal?.companyName ||
                    data?.anchor?.name ||
                    data?.name ||
                    'Sem Nome';
                const docNum = data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '';

                if (norm(name).includes(lowerTerm) || String(docNum || '').includes(t)) {
                    resultsBuffer.push({
                        id: data.id,
                        name: name,
                        type: 'CLIENTE',
                        doc: docNum,
                        icon: 'fa-user'
                    });
                }
            });

            const baseProc = Array.isArray(cache?.processes) ? cache.processes : [];
            baseProc.forEach((data) => {
                const cnj = data?.numeroCNJ || '';
                const parte = data?.parteContraria || '';
                const clientName = data?.clientName || '';

                if (
                    String(cnj || '').includes(t) ||
                    norm(parte).includes(lowerTerm) ||
                    norm(clientName).includes(lowerTerm)
                ) {
                    resultsBuffer.push({
                        id: data.id,
                        name: `${clientName} vs ${parte}`,
                        type: 'PROCESSO',
                        doc: cnj,
                        icon: 'fa-gavel'
                    });
                }
            });

            if (options.autoPickSingle && resultsBuffer.length === 1) {
                try {
                    onSelect && onSelect(resultsBuffer[0]);
                } finally {
                    setResults([]);
                    setTerm('');
                }
                return;
            }

            setResults(resultsBuffer.slice(0, 60));
        } catch (e) {
            console.error(e);
            setResults([]);
        } finally {
            setSearching(false);
        }
    };

    const openPicker = useCallback(
        async (initialTerm) => {
            try {
                await loadCache();
            } catch {}
            setPickerTerm(String(initialTerm || ''));
            setPickerOpen(true);
            setResults([]);
        },
        []
    );

    const PickerModal = ({ isOpen }) => {
        const [localTerm, setLocalTerm] = useState('');

        useEffect(() => {
            if (!isOpen) return;
            setLocalTerm(String(pickerTerm || ''));
        }, [isOpen]);

        const rows = useMemo(() => {
            const cacheClients = Array.isArray(clientsCacheRef.current) ? clientsCacheRef.current : [];
            const cacheProc = Array.isArray(processCacheRef.current) ? processCacheRef.current : [];

            const t = String(localTerm || '').trim();
            const lowerTerm = norm(t);

            const out = [];

            const pushClient = (data) => {
                const name =
                    data?.qualification?.physical?.name ||
                    data?.qualification?.legal?.companyName ||
                    data?.anchor?.name ||
                    data?.name ||
                    'Sem Nome';
                const docNum = data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '';
                out.push({
                    id: data.id,
                    name: name,
                    type: 'CLIENTE',
                    doc: docNum,
                    icon: 'fa-user'
                });
            };

            const pushProc = (data) => {
                const cnj = data?.numeroCNJ || '';
                const parte = data?.parteContraria || '';
                const clientName = data?.clientName || '';
                out.push({
                    id: data.id,
                    name: `${clientName} vs ${parte}`,
                    type: 'PROCESSO',
                    doc: cnj,
                    icon: 'fa-gavel'
                });
            };

            if (!t) {
                cacheClients.slice(0, 120).forEach(pushClient);
                cacheProc.slice(0, 80).forEach(pushProc);
                return out;
            }

            cacheClients.forEach((data) => {
                const name =
                    data?.qualification?.physical?.name ||
                    data?.qualification?.legal?.companyName ||
                    data?.anchor?.name ||
                    data?.name ||
                    'Sem Nome';
                const docNum = data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '';

                if (norm(name).includes(lowerTerm) || String(docNum || '').includes(t)) {
                    pushClient(data);
                }
            });
            cacheProc.forEach((data) => {
                const cnj = data?.numeroCNJ || '';
                const parte = data?.parteContraria || '';
                const clientName = data?.clientName || '';

                if (String(cnj || '').includes(t) || norm(parte).includes(lowerTerm) || norm(clientName).includes(lowerTerm)) {
                    pushProc(data);
                }
            });

            return out;
        }, [localTerm]);

        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 z-[650] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden border border-gray-200">
                    <div className="bg-[#20243B] p-4 text-white flex justify-between items-center">
                        <div className="min-w-0">
                            <h3 className="font-extrabold text-sm uppercase tracking-widest truncate">Selecionar Cliente/Processo</h3>
                            <div className="text-[10px] text-slate-200 mt-1">Use a busca e clique para vincular.</div>
                        </div>
                        <button
                            onClick={() => setPickerOpen(false)}
                            type="button"
                            className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-white/15 bg-white/10 text-white/80 hover:bg-white/15 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/25"
                            title="Fechar"
                        >
                            <i className="fas fa-times" />
                        </button>
                    </div>

                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                        <div className="relative">
                            <input
                                className="w-full pl-9 pr-4 py-2 rounded-lg text-xs outline-none border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30"
                                placeholder="Nome, CPF/CNPJ, Réu ou Número CNJ..."
                                value={localTerm}
                                onChange={(e) => setLocalTerm(e.target.value)}
                                autoFocus
                            />
                            <i className="fas fa-search absolute left-3 top-2.5 text-gray-400 text-xs" />
                        </div>
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
                        {cacheLoading ? (
                            <div className="text-center p-10">
                                <i className="fas fa-spinner fa-spin text-2xl text-brand-blue" />
                            </div>
                        ) : rows.length === 0 ? (
                            <div className="p-10 text-center text-xs text-gray-400">Nenhum registro encontrado.</div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {rows.slice(0, 250).map((res) => (
                                    <button
                                        key={String(res.id)}
                                        type="button"
                                        className="w-full text-left hover:bg-blue-50 transition-colors focus:outline-none focus:bg-blue-50"
                                        onClick={() => {
                                            onSelect && onSelect(res);
                                            setPickerOpen(false);
                                            setResults([]);
                                            setTerm('');
                                        }}
                                    >
                                        <div className="flex items-center gap-3 p-4">
                                            <div className={`w-10 h-10 rounded flex items-center justify-center ${res.type === 'PROCESSO' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                                <i className={`fas ${res.icon}`}></i>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="font-bold text-gray-800 text-sm truncate" title={res.name}>{res.name}</div>
                                                <div className="text-[10px] text-gray-400 font-mono truncate">{res.type} â€¢ {res.doc}</div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        const t = setTimeout(() => {
            handleSearch();
        }, 250);
        return () => clearTimeout(t);
    }, [term]);

    useEffect(() => {
        if (typeof document === 'undefined') return;
        const onDown = (e) => {
            const el = rootRef.current;
            if (el && el.contains(e.target)) return;
            setResults([]);
        };
        document.addEventListener('mousedown', onDown, true);
        document.addEventListener('touchstart', onDown, true);
        return () => {
            document.removeEventListener('mousedown', onDown, true);
            document.removeEventListener('touchstart', onDown, true);
        };
    }, []);

    const hasTerm = Boolean(String(term || '').trim());

    return (
        <div className="relative" ref={rootRef}>
            <div className="flex gap-2">
                <input 
                    className="w-full p-2 border border-gray-300 rounded text-sm outline-none focus:border-brand-blue"
                    placeholder="Nome, CPF, Réu ou Número CNJ..."
                    value={term}
                    onChange={e => setTerm(e.target.value)}
                    onFocus={() => loadCache()}
                    onKeyDown={async (e) => {
                        if (e.key !== 'Enter') return;
                        const t = String(term || '').trim();
                        if (!t) {
                            await openPicker('');
                            return;
                        }
                        try {
                            const cache = await loadCache();
                            const resultsBuffer = [];
                            const lowerTerm = norm(t);

                            const baseClients = Array.isArray(cache?.clients) ? cache.clients : [];
                            baseClients.forEach((data) => {
                                const name =
                                    data?.qualification?.physical?.name ||
                                    data?.qualification?.legal?.companyName ||
                                    data?.anchor?.name ||
                                    data?.name ||
                                    'Sem Nome';
                                const docNum = data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '';

                                if (norm(name).includes(lowerTerm) || String(docNum || '').includes(t)) {
                                    resultsBuffer.push({
                                        id: data.id,
                                        name: name,
                                        type: 'CLIENTE',
                                        doc: docNum,
                                        icon: 'fa-user'
                                    });
                                }
                            });

                            const baseProc = Array.isArray(cache?.processes) ? cache.processes : [];
                            baseProc.forEach((data) => {
                                const cnj = data?.numeroCNJ || '';
                                const parte = data?.parteContraria || '';
                                const clientName = data?.clientName || '';

                                if (
                                    String(cnj || '').includes(t) ||
                                    norm(parte).includes(lowerTerm) ||
                                    norm(clientName).includes(lowerTerm)
                                ) {
                                    resultsBuffer.push({
                                        id: data.id,
                                        name: `${clientName} vs ${parte}`,
                                        type: 'PROCESSO',
                                        doc: cnj,
                                        icon: 'fa-gavel'
                                    });
                                }
                            });

                            if (resultsBuffer.length === 1) {
                                onSelect && onSelect(resultsBuffer[0]);
                                setResults([]);
                                setTerm('');
                                return;
                            }
                        } catch {}

                        await openPicker(t);
                    }}
                    autoFocus
                />
                <button
                    onClick={async () => {
                        const t = String(term || '').trim();
                        if (!t) {
                            await openPicker('');
                            return;
                        }

                        try {
                            const cache = await loadCache();
                            const resultsBuffer = [];
                            const lowerTerm = norm(t);

                            const baseClients = Array.isArray(cache?.clients) ? cache.clients : [];
                            baseClients.forEach((data) => {
                                const name =
                                    data?.qualification?.physical?.name ||
                                    data?.qualification?.legal?.companyName ||
                                    data?.anchor?.name ||
                                    data?.name ||
                                    'Sem Nome';
                                const docNum = data?.qualification?.physical?.cpf || data?.qualification?.legal?.cnpj || '';

                                if (norm(name).includes(lowerTerm) || String(docNum || '').includes(t)) {
                                    resultsBuffer.push({
                                        id: data.id,
                                        name: name,
                                        type: 'CLIENTE',
                                        doc: docNum,
                                        icon: 'fa-user'
                                    });
                                }
                            });

                            const baseProc = Array.isArray(cache?.processes) ? cache.processes : [];
                            baseProc.forEach((data) => {
                                const cnj = data?.numeroCNJ || '';
                                const parte = data?.parteContraria || '';
                                const clientName = data?.clientName || '';

                                if (
                                    String(cnj || '').includes(t) ||
                                    norm(parte).includes(lowerTerm) ||
                                    norm(clientName).includes(lowerTerm)
                                ) {
                                    resultsBuffer.push({
                                        id: data.id,
                                        name: `${clientName} vs ${parte}`,
                                        type: 'PROCESSO',
                                        doc: cnj,
                                        icon: 'fa-gavel'
                                    });
                                }
                            });

                            if (resultsBuffer.length === 1) {
                                onSelect && onSelect(resultsBuffer[0]);
                                setResults([]);
                                setTerm('');
                                return;
                            }
                        } catch {}

                        await openPicker(t);
                    }}
                    className="bg-gray-100 px-3 rounded text-gray-600 hover:bg-gray-200"
                    type="button"
                    title="Abrir CRM"
                >
                    {searching ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-search"></i>}
                </button>
            </div>

            {cacheLoading ? (
                <div className="absolute top-full left-0 w-full bg-white border p-2 text-xs text-gray-400 italic text-center shadow-lg z-50">Carregando base...</div>
            ) : null}
            {results.length > 0 && (
                <div className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded shadow-lg mt-1 z-[620] max-h-72 overflow-y-auto custom-scrollbar">
                    {results.map(res => (
                        <div key={res.id} onClick={() => { onSelect(res); setResults([]); setTerm(''); }} className="p-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 text-xs flex items-center gap-2">
                            <div className={`w-8 h-8 rounded flex items-center justify-center ${res.type === 'PROCESSO' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                <i className={`fas ${res.icon}`}></i>
                            </div>
                            <div className="flex flex-col overflow-hidden">
                                <div className="font-bold text-gray-700 text-sm truncate">{res.name}</div>
                                <div className="text-[10px] text-gray-400 font-mono truncate mt-0.5">{res.type} â€¢ {res.doc}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {hasTerm && results.length === 0 && !searching && !cacheLoading ? (
                 <div className="absolute top-full left-0 w-full bg-white border p-2 text-xs text-gray-400 italic text-center shadow-lg z-50">Nada encontrado.</div>
            ) : null}

            <PickerModal isOpen={pickerOpen} />
        </div>
    );
};

const LinkTriageModal = ({ docRow, onClose }) => {
    const [selectedContext, setSelectedContext] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleLink = async () => {
        const targetDocId = String(docRow?.id || '').trim();
        const ctx = normalizeSelectedContext(selectedContext);

        if (!targetDocId || !ctx.id) return;
        if (saving) return;

        if (ctx.isProcess) {
            setError('Vínculo por processo foi centralizado na Central de Uploads para garantir a pasta estrutural Autos -> Peça -> Anexos. Use o fluxo atual para arquivamento processual.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            let targetClientId = '';
            if (ctx.isClient) {
                targetClientId = ctx.id;
            } else if (ctx.isProcess) {
                targetClientId = await resolveProcessClientId(ctx.id);
            }

            const payload = buildLinkedDocumentPayload({ docRow, selectedContext: selectedContext || ctx, targetClientId });

            await updateDocument(targetDocId, payload, appId);

            if (targetClientId) {
                const actorName = String(
                    auth?.currentUser?.displayName ||
                    auth?.currentUser?.email ||
                    ''
                ).trim() || 'Sistema';
                const notePayload = {
                    fileName: String(docRow?.name || '').trim() || 'Sem nome',
                    text: 'Documento vinculado (saída da Triagem)',
                    workspaceLabel: 'Vinculação de Documento',
                };
                if (ctx.normalizedType) notePayload.relatedEntityType = ctx.normalizedType;
                if (ctx.id) notePayload.relatedEntityId = ctx.id;

                try {
                    await addClientNote(targetClientId, {
                        category: 'SYSTEM',
                        type: 'DOC_UPLOADED',
                        payload: notePayload,
                        actor: { name: actorName, isBot: actorName === 'Sistema' },
                                            }, appId);
                } catch {}
            }

            try {
                window.dispatchEvent(new CustomEvent('rda:open-client', {
                    detail: { clientId: targetClientId || ctx.id, clientName: ctx.name || '' }
                }));
            } catch {}

            onClose && onClose();
        } catch (e) {
            console.error(e);
            setError(String(e?.message || e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-visible border-t-4 border-brand-blue">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <h3 className="font-bold text-gray-700 text-sm"><i className="fas fa-link mr-2 text-brand-blue"></i> Vincular Documento (Triagem)</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-red-500" type="button"><i className="fas fa-times"></i></button>
                </div>

                <div className="p-6 space-y-4">
                    <div className="text-xs text-gray-700 font-bold truncate" title={String(docRow?.name || '')}>{String(docRow?.name || '').trim() || 'Documento'}</div>

                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-bold text-amber-900">
                        Este vinculador permanece disponível apenas para vínculo lógico com cliente. Para arquivamento em processo, use a Central de Uploads.
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">Vincular a (Cliente ou Processo)</label>
                        {selectedContext ? (
                            <div className="flex items-center justify-between bg-green-50 p-2 rounded border border-green-200">
                                <div className="text-xs text-green-800 font-bold flex items-center gap-2">
                                    <i className={selectedContext.icon || "fas fa-link"}></i> {selectedContext.name}
                                </div>
                                <button onClick={() => setSelectedContext(null)} className="text-green-600 hover:text-red-500" type="button"><i className="fas fa-times"></i></button>
                            </div>
                        ) : (
                            <ContextSearch onSelect={setSelectedContext} />
                        )}
                    </div>

                    {error ? (
                        <div className="text-[11px] font-bold text-red-600">{error}</div>
                    ) : null}
                </div>

                <div className="p-4 bg-gray-50 border-t flex justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 hover:shadow-md rounded-lg transition-transform active:scale-95" type="button">Cancelar</button>
                    <button
                        onClick={handleLink}
                        disabled={saving || !selectedContext}
                        className="px-6 py-2.5 text-xs font-bold text-white bg-[#20243B] border border-[#20243B] shadow-md hover:bg-[#1a1e30] hover:shadow-lg rounded-lg flex items-center gap-2 transition-transform active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                        {saving ? 'VINCULANDO...' : 'VINCULAR'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const UploadWizard = ({ file, onCancel, onComplete }) => {
    const [category, setCategory] = useState('OUTROS');
    const [selectedContext, setSelectedContext] = useState(null);
    const [isSealed, setIsSealed] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [driveStatus, setDriveStatus] = useState('');
    const [cadernoId, setCadernoId] = useState(''); // Add cadernoId state
    const [cadernoTitle, setCadernoTitle] = useState(''); // Add cadernoTitle state

    const CATEGORIES = [
        { id: 'PETICAO', label: 'Petição / Peça', icon: 'fa-file-signature', color: 'text-purple-600 border-purple-200 bg-purple-50' },
        { id: 'PROCURACAO', label: 'Procuração', icon: 'fa-id-card', color: 'text-blue-600 border-blue-200 bg-blue-50' },
        { id: 'CONTRATO', label: 'Contrato', icon: 'fa-file-contract', color: 'text-orange-600 border-orange-200 bg-orange-50' },
        { id: 'PROVA', label: 'Meio de Prova', icon: 'fa-balance-scale', color: 'text-red-600 border-red-200 bg-red-50' },
        { id: 'FATURA', label: 'Financeiro', icon: 'fa-file-invoice-dollar', color: 'text-green-600 border-green-200 bg-green-50' },
        { id: 'OUTROS', label: 'Outros', icon: 'fa-file', color: 'text-gray-600 border-gray-200 bg-gray-50' }
    ];

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);
        setSaveError('');

        const ctx = normalizeSelectedContext(selectedContext);
        if (ctx.isProcess) {
            setSaveError('Upload orientado a processo foi centralizado na Central de Uploads para garantir GED + Drive estrutural. Aqui o fluxo permanece apenas como cadastro/vínculo lógico.');
            setSaving(false);
            return;
        }
        let targetClientId = '';
        if (ctx.isClient) {
            targetClientId = ctx.id;
        } else if (ctx.isProcess) {
            targetClientId = await resolveProcessClientId(ctx.id);
        }
        const relatedEntityIds = Array.from(new Set([ctx.id, targetClientId].filter(Boolean)));
        const relatedEntities = Array.from(
            new Map(
                [
                    targetClientId ? { id: targetClientId, type: 'client', name: ctx.isClient ? (ctx.name || null) : null } : null,
                    ctx.id ? { id: ctx.id, type: ctx.isProcess ? 'matter' : 'client', name: ctx.name || null } : null,
                ]
                .filter(Boolean)
                .map((item) => [String(item.id).trim(), item])
            ).values()
        );

        let storageEngineData = null;
        try {
            setDriveStatus('Enviando arquivo para o Google Drive...');
            const backendBase = (() => {
                const env = String(import.meta?.env?.VITE_BACKEND_URL || '').trim();
                if (env) return env.replace(/\/+$/, '');
                return 'https://rda-backend-thuq.onrender.com';
            })();
            const token = await getToken();
            if (!token) throw new Error('Usuário não autenticado para upload.');
            const dynamicParentId = ctx.driveParentId;
            if (!dynamicParentId) {
                setSaveError('Pasta do Google Drive não configurada para este cliente. Acesse os Cadernos de Autos e inicialize a estrutura de pastas antes de fazer upload.');
                setSaving(false);
                setDriveStatus('');
                return;
            }
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('fileName', file.name);
            formData.append('parentId', dynamicParentId);
            formData.append('makePublic', 'true');
            const res = await fetch(`${backendBase}/drive/upload-file`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            if (res.ok) {
                const result = await res.json();
                storageEngineData = {
                    provider: 'gdrive',
                    fileId: String(result.id || result.fileId || '').trim(),
                    id: String(result.id || result.fileId || '').trim(),
                    webViewLink: String(result.webViewLink || '').trim(),
                    webContentLink: String(result.webContentLink || '').trim(),
                };
                setDriveStatus('');
            } else {
                setDriveStatus('');
            }
        } catch {
            setDriveStatus('');
        }

        if (!storageEngineData && file) {
            try {
                setDriveStatus('Salvando arquivo no armazenamento seguro...');
                const safeClientId = String(targetClientId || 'sem-cliente').trim();
                const fileName = `${Date.now()}_${String(file.name || 'arquivo').replace(/[/\\]/g, '_')}`;
                const path = `ged_uploads/${safeClientId}/${fileName}`;
                const ref = storageRef(storage, path);
                await uploadBytes(ref, file, { contentType: file.type || 'application/octet-stream' });
                const downloadUrl = await getDownloadURL(ref);
                storageEngineData = {
                    provider: 'firebase_storage',
                    storagePath: path,
                    webViewLink: downloadUrl,
                    webContentLink: downloadUrl,
                    fileId: '',
                    id: '',
                };
                setDriveStatus('');
            } catch {
                setDriveStatus('');
            }
        }

        const docData = {
            name: file.name,
            size: (file.size / 1024).toFixed(2) + ' KB',
            type: file.type || 'application/unknown',
            category,
            isSealed,
            relatedEntities,
            context: {
                relatedEntities: relatedEntityIds,
                primaryEntityId: ctx.id || null,
                primaryEntityName: ctx.name || null,
                primaryEntityType: ctx.normalizedType || null,
            },
            primaryEntityId: ctx.id || null,
            primaryEntityName: ctx.name || null,
            primaryEntityType: ctx.normalizedType || null,
            clientId: targetClientId || null,
            matterId: ctx.isProcess ? ctx.id : null,
            linkedClient: ctx.name || null,
            cadernoId: String(cadernoId || '').trim() || null, // Pass cadernoId into docData payload
            cadernoTitle: String(cadernoTitle || '').trim() || null, // Pass cadernoTitle into docData payload
            ...(storageEngineData ? {
                storageEngineData,
                fileUrl: storageEngineData.webViewLink || '',
            } : {}),
        };

        try {
            const result = await saveLegalDocument(docData, auth?.currentUser?.email || null);
            if (result.success) {
                if (targetClientId) {
                    const notePayload = {
                        fileName: String(file?.name || '').trim() || 'Sem nome',
                        text: 'Documento anexado na Central de Uploads',
                    };
                    if (ctx.normalizedType) notePayload.relatedEntityType = ctx.normalizedType;
                    if (ctx.id) notePayload.relatedEntityId = ctx.id;

                    try {
                        await addClientNote(targetClientId, {
                            category: 'SYSTEM',
                            type: 'DOC_UPLOADED',
                            payload: notePayload,
                            actor: { name: 'Sistema', isBot: true },
                                                    }, appId);
                    } catch {}
                }

                onComplete();
            } else {
                setSaveError(String(result.error || 'Erro desconhecido'));
                setSaving(false);
            }
        } catch (e) {
            console.error(e);
            setSaveError(String(e?.message || 'Erro desconhecido'));
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in-up">
            <div className="w-full max-w-lg">
                <StepWizard
                    title="Assistente de Ingestão"
                    steps={['Classificar', 'Vincular', 'Confirmar']}
                    className="shadow-2xl"
                    onClose={onCancel}
                >
                    <StepPanel>
                        <div className="space-y-4">
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-bold text-amber-900">
                                Para preservar a coerência entre GED e Google Drive estrutural, anexos destinados a processo devem ser promovidos pela Central de Uploads.
                            </div>
                            <div className="flex items-center gap-4 bg-blue-50 p-3 rounded-lg border border-blue-100">
                                <div className="w-12 h-12 bg-white rounded flex items-center justify-center text-2xl text-blue-600 shadow-sm">
                                    <i className={file.name.endsWith('.pdf') ? "fas fa-file-pdf" : "fas fa-file-alt"}></i>
                                </div>
                                <div className="overflow-hidden">
                                    <p className="font-bold text-sm text-blue-900 truncate">{file.name}</p>
                                    <p className="text-xs text-blue-500">{(file.size/1024).toFixed(2)} KB â€¢ Pronto para classificação</p>
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">O que é este documento?</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {CATEGORIES.map(cat => (
                                        <button
                                            key={cat.id}
                                            onClick={() => setCategory(cat.id)}
                                            className={`px-3 py-2.5 rounded-lg text-xs font-bold border text-left transition-all flex items-center gap-2 shadow-sm active:scale-95 ${category === cat.id ? `${cat.color} ring-2 ring-offset-1 shadow-md` : 'bg-white text-gray-600 border-gray-200 hover:border-[#20243B] hover:text-[#20243B] hover:shadow-md'}`}
                                        >
                                            <i className={`fas ${cat.icon} w-4`}></i> {cat.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </StepPanel>

                    <StepPanel>
                        <div className="space-y-3">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-2">A quem pertence? (Opcional)</label>
                            {selectedContext ? (
                                <div className="flex items-center justify-between bg-green-50 p-2 rounded border border-green-200">
                                    <div className="text-xs text-green-800 font-bold flex items-center gap-2">
                                        <i className={selectedContext.icon || "fas fa-link"}></i> {toTitleCasePt(selectedContext.name)}
                                    </div>
                                    <button onClick={() => setSelectedContext(null)} className="text-green-600 hover:text-red-500"><i className="fas fa-times"></i></button>
                                </div>
                            ) : (
                                <ContextSearch onSelect={setSelectedContext} />
                            )}
                            <p className="text-[10px] text-gray-400 mt-1">* Se vazio, irá para a "Mesa de Triagem".</p>
                            {selectedContext && normalizeSelectedContext(selectedContext).isClient && (
                                <div className="mt-3">
                                    <CadernoSelector // Inject CadernoSelector UI into Step 2
                                        clientId={normalizeSelectedContext(selectedContext).id}
                                        value={cadernoId}
                                        onChange={(id, title) => { setCadernoId(id); setCadernoTitle(title); }}
                                        label="Vincular a Caderno de Autos (opcional)"
                                    />
                                </div>
                            )}
                        </div>
                    </StepPanel>

                    <StepPanel
                        cta={saving ? 'PROCESSANDO...' : 'CONFIRMAR UPLOAD'}
                        onExecute={handleSave}
                        executing={saving}
                    >
                        <div className="space-y-4">
                            <label className="flex items-center gap-2 cursor-pointer bg-gray-50 p-3 rounded border border-gray-200 hover:bg-gray-100 transition-colors">
                                <input type="checkbox" checked={isSealed} onChange={e => setIsSealed(e.target.checked)} className="rounded text-red-600 focus:ring-red-500" />
                                <span className={`text-xs font-bold ${isSealed ? 'text-red-600' : 'text-gray-600'}`}>
                                    <i className="fas fa-lock mr-1"></i> Marcar como Segredo de Justiça
                                </span>
                            </label>
                            {driveStatus ? (
                                <div className="text-[11px] font-bold text-blue-600 flex items-center gap-2">
                                    <i className="fas fa-spinner fa-spin"></i> {driveStatus}
                                </div>
                            ) : null}
                            {saveError ? (
                                <div className="text-[11px] font-bold text-red-600">Erro no upload: {saveError}</div>
                            ) : null}
                            <button onClick={onCancel} className="w-full px-5 py-2 text-xs font-bold text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 rounded-lg transition-transform active:scale-95" type="button">
                                DESCARTAR
                            </button>
                        </div>
                    </StepPanel>
                </StepWizard>
            </div>
        </div>
    );
};

const UploadCenter = () =>
 {
    const [files, setFiles] = useState([]);
    const [wizardFile, setWizardFile] = useState(null);

    const [linkTarget, setLinkTarget] = useState(null);

    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    useEffect(() => {
        try {
            const unsubscribe = watchDocuments(appId, (docs) => {
                setFiles(docs.map((data) => {
                    const displayState = deriveUploadCenterDisplayState(data);
                    return {
                        ...data,
                        ownerName: displayState.ownerName,
                        status: displayState.status,
                        isLinked: displayState.isLinked
                    };
                }));
            });
            return () => unsubscribe();
        } catch (e) { console.error("Erro no UploadCenter:", e); }
    }, []);

    const onFileSelect = (e) => {
        if (e.target.files[0]) {
            setWizardFile(e.target.files[0]); // Abre o Wizard
        }
    };

    const handleDelete = async (id) => {
        const targetId = String(id || '').trim();
        if (!targetId) return;
        if (deleteBusy) return;

        setDeleteBusy(true);
        try {
            await deleteDocument(targetId, appId);
            setDeleteTargetId(null);
        } catch (e) {
            console.error('Erro ao excluir:', e);
        } finally {
            setDeleteBusy(false);
        }
    };

    const getIconClass = (cat) => {
        const map = {
            'PETICAO': 'bg-purple-100 text-purple-600',
            'PROCURACAO': 'bg-blue-100 text-blue-600',
            'CONTRATO': 'bg-orange-100 text-orange-600',
            'FATURA': 'bg-green-100 text-green-600',
            'PROVA': 'bg-red-100 text-red-600',
            'LEGADO': 'bg-gray-100 text-gray-500'
        };
        return map[cat] || 'bg-gray-100 text-gray-500';
    };

    return (
        <div className="p-8 h-full bg-white flex flex-col overflow-hidden relative">
            {/* ÃREA DE DROP */}
            <div className="mb-8 p-10 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 flex flex-col items-center justify-center text-center hover:bg-blue-50 hover:border-blue-300 transition-colors relative cursor-pointer flex-shrink-0 group">
                <input 
                    type="file" 
                    onChange={onFileSelect} 
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                />
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4 group-hover:scale-110 transition-transform">
                    <i className="fas fa-cloud-upload-alt text-3xl text-brand-blue"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-700">Central de Ingestão Inteligente</h3>
                <p className="text-xs text-gray-400 mt-1 max-w-md">
                    Arraste arquivos para iniciar o <strong>Assistente de Classificação</strong>. 
                </p>
            </div>

            <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="text-sm font-bold text-gray-500 uppercase flex items-center gap-2">
                    <i className="fas fa-layer-group"></i> Repositório ({files.length})
                </h3>
                <span className="text-[10px] bg-gray-100 px-2 py-1 rounded text-gray-500 font-bold">V.4.2 GRAPH</span>
            </div>
            
            <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-2">
                {files.length === 0 && (
                    <div className="flex flex-col items-center justify-center h-40 text-gray-300">
                        <i className="far fa-folder-open text-4xl mb-2"></i>
                        <p className="text-sm">Nenhum documento encontrado.</p>
                    </div>
                )}
                
                {files.map(file => (
                    <div key={file.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all group">
                        <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded flex items-center justify-center text-lg ${getIconClass(file.category)}`}>
                                <i className={`fas fa-file-alt`}></i>
                            </div>
                            <div>
                                <p className="font-bold text-gray-800 text-sm truncate max-w-[300px]">{file.name}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${getIconClass(file.category)} bg-opacity-20`}>
                                        {file.category}
                                    </span>
                                    {file.ownerName !== 'Sem Dono' && (
                                        <span className="text-[9px] font-bold bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded flex items-center gap-1">
                                            <i className="fas fa-link"></i> {toTitleCasePt(file.ownerName)}
                                        </span>
                                    )}
                                    {file.isSealed && <span className="text-[9px] text-red-500 font-bold flex items-center gap-1"><i className="fas fa-lock"></i> SIGILO</span>}
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <span className={`text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide ${file.status === 'VINCULADO' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800'}`}>
                                {file.status}
                            </span>

                            {!file.isLinked ? (
                                <button
                                    type="button"
                                    onClick={() => setLinkTarget(file)}
                                    className="text-[10px] font-bold px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-100"
                                >
                                    Vincular
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setLinkTarget(file)}
                                    className="text-[10px] font-bold px-2 py-1 rounded bg-gray-50 text-gray-600 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200 border border-gray-200 transition-colors"
                                    title="Alterar vínculo"
                                >
                                    Alterar
                                </button>
                            )}

                            {String(deleteTargetId || '') === String(file.id || '') ? (
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setDeleteTargetId(null)}
                                        className="text-[10px] font-bold px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        disabled={deleteBusy}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(file.id)}
                                        className="text-[10px] font-bold px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                                        disabled={deleteBusy}
                                    >
                                        {deleteBusy ? 'Excluindo...' : 'Excluir'}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setDeleteTargetId(file.id)}
                                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-2 rounded transition-colors"
                                    title="Excluir"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* WIZARD MODAL */}
            {wizardFile && (
                <UploadWizard 
                    file={wizardFile} 
                    onCancel={() => setWizardFile(null)} 
                    onComplete={() => setWizardFile(null)} 
                />
            )}

            {linkTarget ? (
                <LinkTriageModal
                    docRow={linkTarget}
                    onClose={() => setLinkTarget(null)}
                />
            ) : null}
        </div>
    );
};

// --- COMPONENTE PRINCIPAL: HUB DE DOCUMENTOS ---
const DocumentFactoryHub = ({ activeTab }) => {
    // Renderização Defensiva
    const renderContent = () => {
        switch (activeTab) {
            case 'CONTRACTS':
                return ContractFactory ? <ContractFactory /> : <div className="p-10 text-center text-red-500 font-bold">Erro: ContractFactory não carregado.</div>;
            case 'PROCURATION':
                return ProcurationFactory ? <ProcurationFactory /> : <div className="p-10 text-center text-red-500 font-bold">Erro: ProcurationFactory não carregado.</div>;
            case 'UPLOADS':
                return <div className="h-full w-full bg-white"><UploadCenter /></div>;
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col h-full w-full bg-white overflow-hidden relative">
            <div className="flex-1 w-full h-full overflow-hidden relative">
                {renderContent()}
            </div>
        </div>
    );
};

export default DocumentFactoryHub;
